import { useNavigate } from 'react-router-dom';

const StabilityPathway = () => {
  const navigate = useNavigate();

  return (
    <section id="stability-pathway" className="relative bg-[#faf9f7] py-24 sm:py-36 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bronze/25 to-transparent"></div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-16">

        {/* Header label */}
        <div className="flex items-center gap-3 mb-10">
          <div className="w-8 h-[1px] bg-bronze"></div>
          <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
            Stability Pathway
          </span>
        </div>

        {/* Two-column layout: intro text + flow diagram */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 mb-20">

          {/* Left: Intro text */}
          <div>
            <h2 className="font-display text-2xl sm:text-[2.4rem] font-light text-charcoal italic leading-[1.2] mb-8">
              Where to Begin
            </h2>
            <div className="space-y-5 text-charcoal/80 text-[13px] font-sans font-light leading-[1.95]">
              <p>
                If the Stability Method resonates with you, the journey begins with the Stability Signature — a 90-minute entry session.
              </p>
              <p>
                During this first meeting, we quietly observe the deeper patterns your face carries — the subtle imprint of nervous tension, circulation, and internal balance. It is a moment of orientation, where we listen to what your body has been expressing beneath the surface.
              </p>
              <p>
                From this assessment, a tailored treatment pathway is thoughtfully designed. Future appointments are then scheduled in alignment with this plan, allowing change to unfold gradually and with intention.
              </p>
              <p className="text-charcoal/90 italic font-light">
                Begin where clarity begins — with the Stability Signature.
              </p>
            </div>
          </div>

          {/* Right: Minimal flow diagram */}
          <div className="flex flex-col justify-center">
            <div className="relative flex flex-col gap-0">

              {/* Step 1 */}
              <div className="flex items-start gap-5">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-charcoal flex items-center justify-center shadow-sm">
                    <i className="ri-search-eye-line text-bronze text-sm"></i>
                  </div>
                  <div className="w-px h-12 bg-gradient-to-b from-bronze/40 to-bronze/15 mt-1"></div>
                </div>
                <div className="pt-1.5 pb-12">
                  <p className="text-[8px] font-sans font-semibold tracking-[0.3em] uppercase text-bronze mb-1">Step 01</p>
                  <h3 className="font-display text-[1.05rem] font-light text-charcoal italic leading-snug mb-1">
                    Stability Signature
                  </h3>
                  <span className="inline-flex items-center gap-1.5 text-[9px] font-sans text-charcoal/60 font-light">
                    <i className="ri-time-line text-[9px]"></i>
                    90 min Entry Session
                  </span>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-5">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-white border border-warmGray/25 flex items-center justify-center">
                    <i className="ri-route-line text-charcoal/40 text-sm"></i>
                  </div>
                  <div className="w-px h-12 bg-gradient-to-b from-bronze/15 to-bronze/10 mt-1"></div>
                </div>
                <div className="pt-1.5 pb-12">
                  <p className="text-[8px] font-sans font-semibold tracking-[0.3em] uppercase text-bronze/50 mb-1">Step 02</p>
                  <h3 className="font-display text-[1.05rem] font-light text-charcoal/75 italic leading-snug mb-1">
                    Personalized Stability Treatments
                  </h3>
                  <span className="inline-flex items-center gap-1.5 text-[9px] font-sans text-charcoal/50 font-light">
                    <i className="ri-sparkling-2-line text-[9px]"></i>
                    Bespoke Series
                  </span>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-5">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-white border border-warmGray/25 flex items-center justify-center">
                    <i className="ri-leaf-line text-charcoal/30 text-sm"></i>
                  </div>
                </div>
                <div className="pt-1.5">
                  <p className="text-[8px] font-sans font-semibold tracking-[0.3em] uppercase text-bronze/40 mb-1">Step 03</p>
                  <h3 className="font-display text-[1.05rem] font-light text-charcoal/65 italic leading-snug mb-1">
                    Ongoing Regulation &amp; Maintenance
                  </h3>
                  <span className="inline-flex items-center gap-1.5 text-[9px] font-sans text-charcoal/45 font-light">
                    <i className="ri-calendar-check-line text-[9px]"></i>
                    Long-Term Stability
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-14">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-bronze/20"></div>
          <div className="w-1 h-1 rounded-full bg-bronze/35"></div>
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-bronze/20"></div>
        </div>

        {/* Signature Entry Service Card */}
        <div className="group relative bg-white border border-warmGray/20 overflow-hidden hover:border-bronze/30 hover:shadow-md transition-all duration-400">
          <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-bronze via-bronze/50 to-transparent"></div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-0">

            {/* Left content */}
            <div className="px-10 py-10">
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="text-[7.5px] font-sans font-semibold tracking-[0.35em] uppercase text-bronze">
                  Stability Signature
                </span>
                <div className="w-4 h-px bg-bronze/30"></div>
                <span className="inline-flex items-center gap-2 bg-bronze/10 border border-bronze/25 px-3 py-1.5 rounded-sm">
                  <i className="ri-time-line text-bronze text-[10px]"></i>
                  <span className="text-[9px] font-sans font-semibold tracking-[0.15em] uppercase text-charcoal/70">90 min</span>
                  <span className="w-px h-3 bg-bronze/30"></span>
                  <span className="text-[9px] font-sans font-semibold tracking-[0.15em] text-charcoal/80">$320</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-[8px] font-sans font-semibold tracking-[0.2em] uppercase bg-bronze/8 text-bronze px-2.5 py-1">
                  Required — All New Clients
                </span>
              </div>

              <h3 className="font-display text-[1.5rem] font-light text-charcoal italic leading-snug mb-5">
                The Entry Point Into the Vionne Stability Method
              </h3>

              <p className="text-charcoal/70 text-[12px] font-sans font-light leading-[1.9] mb-8 max-w-lg">
                A comprehensive 90-minute intake session that maps your unique pattern of tension, regulation, and structural holding — forming the foundation of everything that follows. Includes assessment, archetype identification, tailored treatment plan, and integrated first treatment.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                {[
                  { icon: 'ri-search-eye-line', label: 'Stability Assessment' },
                  { icon: 'ri-sparkling-2-line', label: 'Archetype Mapping' },
                  { icon: 'ri-file-list-3-line', label: 'Treatment Plan' },
                  { icon: 'ri-leaf-line', label: 'Integrated Treatment' },
                  { icon: 'ri-compass-3-line', label: 'Pathway Guidance' },
                  { icon: 'ri-shield-check-line', label: 'Nervous System Work' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 py-2.5 px-3 bg-[#faf9f7] border border-warmGray/15">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-bronze text-[11px]`}></i>
                    </div>
                    <span className="text-[9.5px] font-sans font-medium text-charcoal/75 leading-snug">{item.label}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/booking')}
                className="group/btn inline-flex items-center gap-4 cursor-pointer"
              >
                <span className="px-9 py-3.5 bg-charcoal text-white text-[9px] font-sans font-semibold tracking-[0.28em] uppercase group-hover/btn:bg-bronze transition-colors duration-300 whitespace-nowrap">
                  Begin your stability.
                </span>
                <span className="w-9 h-9 border border-charcoal/20 flex items-center justify-center group-hover/btn:border-bronze group-hover/btn:bg-bronze transition-all duration-300">
                  <i className="ri-arrow-right-line text-charcoal text-sm group-hover/btn:text-white transition-colors duration-300"></i>
                </span>
              </button>
            </div>

            {/* Right image */}
            <div className="hidden lg:block w-72 xl:w-80 relative overflow-hidden">
              <img
                src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/f52ed806d38a81da3c5b5fc9c8a60573.png"
                alt="Stability Signature Session"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent"></div>
              <div className="absolute bottom-6 right-6 text-right">
                <p className="text-white text-[9px] font-sans font-semibold tracking-[0.25em] uppercase drop-shadow-sm">$320</p>
              </div>
            </div>

          </div>
        </div>

      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bronze/20 to-transparent"></div>
    </section>
  );
};

export default StabilityPathway;
